import React, { createContext, useContext, useState, useEffect } from 'react';
import { submitLead } from '../firebase/leads';
import { buildLeadFromConsultation } from '../firebase/leadMapper';

const ConsultationContext = createContext();

export const useConsultations = () => {
  const context = useContext(ConsultationContext);
  if (!context) {
    throw new Error('useConsultations must be used within a ConsultationProvider');
  }
  return context;
};

export const ConsultationProvider = ({ children }) => {
  const [consultations, setConsultations] = useState([]);
  const [showToast, setShowToast] = useState({ show: false, message: '', type: '' });

  // Load consultations from localStorage on mount
  useEffect(() => {
    const savedConsultations = localStorage.getItem('consultations');
    if (savedConsultations) {
      const parsedConsultations = JSON.parse(savedConsultations);
      setConsultations(parsedConsultations);
    }
  }, []);

  // Save consultations to localStorage whenever they change
  useEffect(() => {
    if (consultations.length > 0) {
      localStorage.setItem('consultations', JSON.stringify(consultations));
    }
  }, [consultations]);

  const showToastNotification = (message, type) => {
    setShowToast({ show: true, message, type });
    setTimeout(() => {
      setShowToast({ show: false, message: '', type: '' });
    }, 3000);
  };

  const addConsultation = (consultationData) => {
    const consultationWithId = {
      ...consultationData,
      id: Date.now(),
      submittedAt: new Date().toISOString(),
      status: 'pending'
    };
    setConsultations(prev => [...prev, consultationWithId]);
    showToastNotification('Consultation request submitted successfully! We will contact you soon.', 'success');

    const lead = buildLeadFromConsultation(consultationData);
    submitLead({
      name: lead.name,
      email: lead.email,
      phone: lead.phone,
      message: lead.message,
      extraFields: lead.extraFields,
    }).catch((err) => console.error('Firestore lead error:', err));

    return consultationWithId;
  };

  const updateConsultationStatus = (consultationId, status) => {
    setConsultations(prev => 
      prev.map(consultation => 
        consultation.id === consultationId 
          ? { ...consultation, status, updatedAt: new Date().toISOString() }
          : consultation
      )
    );
    showToastNotification(`Consultation status updated to ${status}`, 'success');
  };

  const deleteConsultation = (consultationId) => {
    const consultation = consultations.find(c => c.id === consultationId);
    setConsultations(prev => prev.filter(consultation => consultation.id !== consultationId));
    showToastNotification(`Consultation request deleted`, 'error');
  };

  const value = {
    consultations,
    addConsultation,
    updateConsultationStatus,
    deleteConsultation,
    showToast
  };

  return (
    <ConsultationContext.Provider value={value}>
      {children}
      {showToast.show && (
        <div
          className={`fixed bottom-4 right-4 px-6 py-3 rounded-lg shadow-lg z-50 transition-all duration-300 ${
            showToast.type === 'success' ? 'bg-green-500' : 'bg-red-500'
          } text-white`}
        >
          {showToast.message}
        </div>
      )}
    </ConsultationContext.Provider>
  );
};
